import BaseModel from "./BaseModel";
import {action, makeObservable, observable, reaction} from "mobx";
import {post} from "../common/requester";
import BillingDetail from "./BillingDetail";
import Tournament from "./Tournament";
import Award from "./Award";

export default class Parent extends BaseModel {
    type = "parent";
    company_id = 0;
    id = 0;
    children = [];
    child = {};
    child_id = 0;
    attendance = [];
    billing = [];
    billing_detail = new BillingDetail();
    contest = new Tournament(this, "parent");
    debts = [];
    self = {};
    award = new Award(this);

    constructor(store, type) {
        super(store);
        makeObservable(this, {
            type: observable,
            company_id: observable,
            id: observable,
            self: observable,
            getSelf: action,
            children: observable,
            getChildren: action,
            child_id: observable,
            attendance: observable,
            getAttendance: action,
            getBilling: action,
            billing: observable,
            billing_detail: observable,
            reset_billing_detail: action,
            pay_bill: action,
            contest: observable,
            getChild: action,
            child: observable,
            sendClassPhotos: action,
            getDebts: action,
            debts: observable,
            award: observable,
            getAwards: action,
            getAwardTypes: action,
            createAward: action,
        });

        this.type = type;

        reaction(() => this.company_id, (newId, oldId) => {
            this.contest.setProperty("company_id", newId);
            this.award.setContext({company_id: newId});

            if (oldId && newId !== oldId) {
                this.setProperty("children", []);
                this.setProperty("child", {});
                this.setProperty("child_id", 0);
                this.setProperty("attendance", []);
                this.setProperty("billing", []);
                this.setProperty("debts", []);
                this.reset_billing_detail();
            }
        });

        reaction(() => this.id, (uid) => {
            this.award.setContext({company_user_id: uid});
        });
    }

    async getChildren() {
        if (!this.company_id) {
            this.setProperty("children", []);
            this.setProperty("child_id", 0);
            return;
        }

        const response = await post("parent/get_my_children", {
            company_id: this.company_id
        }).catch(error => console.log(error));

        if (response?.result !== 0) return;

        const children = response.children || [];
        this.setProperty("children", children);

        if (children.length === 0) {
            this.setProperty("child_id", 0);
            return;
        }

        const selectedExists = children.some(child => child.id === this.child_id);
        const childId = selectedExists ? this.child_id : children[0].id;

        this.setProperty("child_id", childId);
        this.contest.gamesQuery.setProperty("company_user_id", childId);
    }

    async getChild() {
        if (!this.company_id) return;

        const response = await post("parent/get_child_self", {
            company_id: this.company_id
        }).catch(error => console.log(error));

        if (response?.result !== 0) return;

        const child = response.child || {};
        this.setProperty("child", child);
        this.setProperty("child_id", child.id || 0);

        if (child.id) {
            this.contest.gamesQuery.setProperty("company_user_id", child.id);
        }
    }

    async getAttendance() {
        if (!this.company_id || !this.child_id) {
            this.setProperty("attendance", []);
            return;
        }

        const response = await post("parent/get_attendance", {
            company_id: this.company_id,
            company_user_id: this.child_id
        }).catch(error => console.log(error));

        if (response?.result === 0) {
            this.setProperty("attendance", response.attendance || []);
        }
    }

    async getBilling() {
        if (!this.company_id || !this.child_id) {
            this.setProperty("billing", []);
            return;
        }

        const response = await post("parent/get_billing", {
            company_id: this.company_id,
            company_user_id: this.child_id
        }).catch(error => console.log(error));

        if (response?.result === 0) {
            this.setProperty("billing", response.billing || []);
        }
    }

    async getBillingDetail(billing_id) {
        if (!this.company_id || !billing_id) return;

        const response = await post("parent/get_billing_detail", {
            company_id: this.company_id,
            billing_id
        }).catch(error => console.log(error));

        if (response?.result === 0) {
            this.billing_detail.fromJson(response.billing_detail || {});
        }
    }

    reset_billing_detail() {
        this.setProperty("billing_detail", new BillingDetail());
    }

    async pay_bill() {
        let ret = {};

        if (this.billing_detail.payment_method === "transfer") {
            if (!this.billing_detail.cheque_upload) {
                return Promise.reject("error");
            }

            ret = await post(
                "parent/pay-with-cheque",
                this.billing_detail
            ).catch(err => console.error("--pay error", err));
        } else {
            ret = await post("parent/pay-cash", {
                billing_id: this.billing_detail.id
            }).catch(err => console.error("--pay error", err));
        }

        if (!ret?.billing) {
            return Promise.reject("error");
        }

        this.reset_billing_detail();
        await this.getBilling();
        await this.getDebts();

        return ret;
    }

    async sendClassPhotos(team_class_id, is_album = true) {
        await post("parent/send_class_photos", {
            company_id: this.company_id,
            team_class_id,
            is_album
        }).catch(error => console.log(error));
    }

    async getDebts() {
        if (!this.company_id || !this.child_id) {
            this.setProperty("debts", []);
            return;
        }

        const response = await post("parent/get_debts", {
            company_id: this.company_id,
            company_user_id: this.child_id
        }).catch(error => console.log(error));

        if (response?.result === 0) {
            this.setProperty("debts", response.debts || []);
        }
    }

    async getSelf() {
        if (!this.company_id) return;

        const response = await post("parent/get_self", {
            company_id: this.company_id
        }).catch(error => console.log(error));

        if (response?.result === 0) {
            this.setProperty("self", response.self || {});
        }
    }

    async getAwardTypes({sport_id, scope, include_common = true} = {}) {
        this.award.setTypeFilter({sport_id, scope, include_common});
        return this.award.getAwardTypes();
    }

    async getAwards(filters = {}) {
        return this.award.listAwards(filters);
    }

    async createAward(payload = {}) {
        return this.award.createAward({
            company_user_id: this.id,
            ...payload
        });
    }
}